import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { US_STATES, formatLabel } from "@/lib/api";
import { useCoverage } from "@/hooks/usePublicConfig";
import { ArrowLeft, ArrowRight, BarChart3 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Side-by-side comparison of two jurisdictions. Free-text picker so it works
 * for US states, "Federal", and any country.
 */
export default function Compare() {
  const { countries: COUNTRIES } = useCoverage();
  const [params, setParams] = useSearchParams();
  const [left, setLeft] = useState(params.get("left") || "Texas");
  const [right, setRight] = useState(params.get("right") || "California");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const ALL = ["Federal", ...US_STATES.filter((s) => s !== "Federal"), ...COUNTRIES];

  useEffect(() => {
    if (!left || !right) return;
    setLoading(true);
    api.get(`/compare`, { params: { left, right } })
      .then((r) => setData(r.data))
      .catch((err) => console.error("Compare failed", err))
      .finally(() => setLoading(false));
    setParams({ left, right }, { replace: true });
  }, [left, right, setParams]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Link to="/jurisdictions" className="inline-flex items-center gap-2 font-mono-plex text-sm uppercase tracking-wider hover:underline">
        <ArrowLeft className="w-4 h-4" /> All jurisdictions
      </Link>

      <div className="mt-4 border-b border-black/15 pb-6">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          <div className="overline">/compare</div>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-2">Side-by-side</h1>
        <p className="mt-3 text-base text-[#333] max-w-2xl">
          Pick two jurisdictions to compare on documented action volume, impact mix, and category breakdown.
        </p>
      </div>

      {/* Picker */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-end mt-8">
        <PickerCol value={left} setValue={setLeft} options={ALL.includes(left) ? ALL : [left, ...ALL]} side="left" />
        <div className="hidden md:block self-center pt-6"><ArrowRight className="w-5 h-5 text-[#888]" /></div>
        <PickerCol value={right} setValue={setRight} options={ALL.includes(right) ? ALL : [right, ...ALL]} side="right" />
      </div>

      {/* Stat panels */}
      {loading && <div className="mt-8 font-mono-plex text-sm uppercase tracking-wider text-[#555]">Loading…</div>}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/15 border border-black/15 mt-8">
          <Panel side="left" data={data.left} />
          <Panel side="right" data={data.right} />
        </div>
      )}
    </div>
  );
}

function PickerCol({ value, setValue, options, side }) {
  return (
    <div className="block">
      <span className="overline mb-1.5 block">/{side}</span>
      <Select value={value} onValueChange={setValue}>
        <SelectTrigger data-testid={`compare-${side}`} className="w-full rounded-none border border-black h-12 px-3 font-display text-lg bg-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="rounded-none max-h-80">
          {options.map((o) => (
            <SelectItem key={o} value={o} data-testid={`compare-${side}-option-${o.replace(/[^a-z0-9]/gi, "-")}`}>{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function Panel({ side, data }) {
  return (
    <div className="bg-white p-6" data-testid={`compare-panel-${side}`}>
      <div className="overline">/{side}</div>
      <h2 className="font-display text-3xl font-black mt-1">{data.jurisdiction}</h2>
      <div className="mt-3 font-display text-5xl font-black">
        {data.total}
        <span className="text-base text-[#555] ml-2 font-mono-plex uppercase tracking-wider">entries</span>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-2">
        <MiniStat label="Restricts" value={data.by_impact.restricts} color="#D32F2F" />
        <MiniStat label="Protects" value={data.by_impact.protects} color="#008A00" />
        <MiniStat label="Mixed" value={data.by_impact.mixed} color="#0050FF" />
      </div>

      <div className="mt-6">
        <div className="overline mb-2">/by category</div>
        {Object.keys(data.by_category).length === 0 ? (
          <p className="text-sm text-[#555]">No entries.</p>
        ) : (
          <ul className="space-y-1.5">
            {Object.entries(data.by_category)
              .sort((a, b) => b[1] - a[1])
              .map(([cat, n]) => (
                <li key={cat} className="flex justify-between border-b border-black/10 py-1">
                  <span className="text-sm">{formatLabel(cat)}</span>
                  <span className="font-mono-plex text-sm">{n}</span>
                </li>
              ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div className="border border-black/10 p-3">
      <div className="font-mono-plex text-[11px] uppercase tracking-wider text-[#555]">{label}</div>
      <div className="font-display text-2xl font-black mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}
